import { useState, useEffect, useRef, useMemo } from 'react';
import { useIsMobile } from '@/core/hooks/useMediaQuery';
import type { CustomizationSelection } from '../../domain/models/CustomizationSelection';

interface UseCustomizationSheetV2Args {
  open: boolean;
  draft: CustomizationSelection;
  onCancel: () => void;
  onOptionChange: (key: string, value: string | number) => void;
}

export function useCustomizationSheetV2({
  open,
  draft,
  onCancel,
  onOptionChange,
}: UseCustomizationSheetV2Args) {
  const isMobile = useIsMobile();
  const [isSizeDropdownOpen, setIsSizeDropdownOpen] = useState(false);
  const [isAnimatedOpen, setIsAnimatedOpen] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const initialDraftRef = useRef(draft);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        setIsAnimatedOpen(true);
      }, 16);
      return () => clearTimeout(timer);
    } else {
      const timer = setTimeout(() => {
        setIsAnimatedOpen(false);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (JSON.stringify(draft) !== JSON.stringify(initialDraftRef.current)) {
      const timer = setTimeout(() => {
        setHasChanged(true);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [draft]);

  const handleClose = () => {
    setIsAnimatedOpen(false);
    setTimeout(() => {
      onCancel();
    }, 300);
  };

  const [localPriceAnimating, setLocalPriceAnimating] = useState(false);
  const localAnimationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleOptionChangeWithAnimation = (key: string, value: string) => {
    onOptionChange(key, value);

    setLocalPriceAnimating(true);
    if (localAnimationTimeoutRef.current) {
      clearTimeout(localAnimationTimeoutRef.current);
    }
    localAnimationTimeoutRef.current = setTimeout(() => {
      setLocalPriceAnimating(false);
      localAnimationTimeoutRef.current = null;
    }, 500);
  };

  const [dragOffset, setDragOffset] = useState(0);
  const touchStartY = useRef(0);
  const isDragging = useRef(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    touchStartY.current = e.touches[0].clientY;
    isDragging.current = true;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile || !isDragging.current) return;
    const currentY = e.touches[0].clientY;
    const diffY = currentY - touchStartY.current;
    if (diffY > 0) {
      setDragOffset(diffY);
    } else {
      setDragOffset(0);
    }
  };

  const handleTouchEnd = () => {
    if (!isMobile || !isDragging.current) return;
    isDragging.current = false;
    if (dragOffset > 100) {
      handleClose();
    }
    setDragOffset(0);
  };

  const transformStyle = useMemo(() => {
    if (!isMobile) return undefined;
    if (dragOffset > 0) {
      return { transform: `translateY(${dragOffset}px)`, transition: 'none' };
    }
    return undefined;
  }, [isMobile, dragOffset]);

  return {
    isMobile,
    isSizeDropdownOpen,
    setIsSizeDropdownOpen,
    isAnimatedOpen,
    hasChanged,
    handleClose,
    localPriceAnimating,
    handleOptionChangeWithAnimation,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    transformStyle,
  };
}
